import React, { useEffect, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { FaCheckSquare, FaDownload, FaEdit, FaPlus, FaRegSquare, FaSearch, FaSort, FaSortDown, FaSortUp, FaTrash } from "react-icons/fa";
import { usePagination, useSortBy, useTable } from "react-table";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import * as XLSX from "xlsx";
import "../../../Styles/QuestionsPage.css"; // Import your CSS file here

const BASE_URL = process.env.REACT_APP_API_URL;


// Add this helper function at the beginning of your component
const cleanText = (text) => {
  if (!text) return '';
  return text.replace(/['"]+/g, '');
};

const FactoryQuestionsPage = () => {
  const [questions, setQuestions] = useState([]);
  const [filteredQuestions, setFilteredQuestions] = useState([]);
  const [questionText, setQuestionText] = useState("");
  const [answerType, setAnswerType] = useState("");
  const [answerOptions, setAnswerOptions] = useState([]);
  const [optionPoints, setOptionPoints] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);

  // Error Modal State
  const [errorModal, setErrorModal] = useState({
    show: false,
    title: "",
    message: ""
  });

  // Delete Confirmation Modal States
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const userData = JSON.parse(localStorage.getItem("user") || "{}");
  const FactoryCode = userData?.FactoryCode;

  // Add these states after your existing state declarations
  const [markedQuestions, setMarkedQuestions] = useState([]);

  // Error Modal Helper
  const showErrorModal = (title, message) => {
    setErrorModal({
      show: true,
      title,
      message
    });
  };

  // First, add this state variable to your component:
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    if (!FactoryCode) {
      showErrorModal("Missing Information", "User Branch Code is missing!");
      return;
    }
    fetchQuestions();
  }, [FactoryCode]);

  // Then update your fetchQuestions function to use loading state:
  const fetchQuestions = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${BASE_URL}/get-factory-questions?FactoryCode=${FactoryCode}`);
      if (!response.ok) {
        throw new Error("Failed to fetch questions");
      }
      const data = await response.json();
      setQuestions(data);
      setFilteredQuestions(data);
    } catch (error) {
      showErrorModal("Fetch Error", "Error fetching questions. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Update handleFileUpload to show loading state:
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file || !file.name.endsWith(".xlsx")) {
      toast.error("Please upload a valid Excel file.");
      return;
    }

    setIsUploading(true);
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const parsedData = XLSX.utils.sheet_to_json(sheet);

        if (parsedData.length === 0) {
          toast.error("The uploaded file is empty.");
          setIsUploading(false);
          return;
        }

        for (const row of parsedData) {
          const requestData = {
            id: `q${(Date.now() % 1000000000)}${Math.floor(Math.random() * 1000)}`,
            questionText: row["Question"] || "",
            answerType: row["Answer Type"] || "",
            answerOptions: row["Answer Options"] ? row["Answer Options"].split(",").map((opt) => opt.trim()).join(",") : "",
            optionPoints: row["Points"] || 0,
            FactoryCode: FactoryCode,
            addingMethod: "manual"
          };

          const response = await fetch(`${BASE_URL}/add-factory-question`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(requestData),
          });

          if (!response.ok) {
            toast.error(`Error adding question: ${row["Question"]}`);
          }
        }

        toast.success("File uploaded successfully!");
        fetchQuestions();
      } catch (error) {
        showErrorModal("Upload Error", "Error uploading data. Please try again.");
      } finally {
        setIsUploading(false);
      }
    };

    reader.readAsArrayBuffer(file);
  };

  const handleSearch = (event) => {
    const searchValue = event.target.value.toLowerCase();
    setSearchText(searchValue);
    const filtered = questions.filter(
      (q) =>
        q.questionText.toLowerCase().includes(searchValue) ||
        q.answerType.toLowerCase().includes(searchValue)
    );
    setFilteredQuestions(filtered);
  };

  // Update the handleSave function:
  const handleSave = async () => {
    if (!FactoryCode) {
      showErrorModal("Missing Information", "User Branch Code is missing!");
      return;
    } 

    if (answerType !== "FreeText") {
      const totalPoints = optionPoints.reduce((sum, points) => sum + Number(points), 0);
      if (totalPoints !== 10) {
        toast.error("Total points must equal 10!");
        return;
      }
    }

    setIsSaving(true);
    const questionId = `q${(Date.now() % 1000000000)}${Math.floor(Math.random() * 1000)}`;
    const data = {
      id: isEditing ? editId : questionId,
      questionText,
      answerType,
      answerOptions: Array.isArray(answerOptions)
        ? answerOptions.join(",")
        : answerOptions,
      optionPoints: answerType === "FreeText" 
        ? null 
        : (Array.isArray(optionPoints) ? optionPoints.join(",") : optionPoints),
      FactoryCode: FactoryCode,
      addingMethod: "manual"
    };

    const url = isEditing
      ? `${BASE_URL}/update-factory-question/${editId}`
      : `${BASE_URL}/add-factory-question`;

    const method = isEditing ? "PUT" : "POST";

    try {
      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        throw new Error("Failed to save question data");
      }
      toast.success(isEditing ? "Question updated successfully!" : "Question added successfully!");
      setShowModal(false);
      resetForm();
      fetchQuestions();
    } catch (error) {
      showErrorModal("Save Error", "Error saving question data. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleEdit = (question) => {
    setEditId(question.id);
    setQuestionText(cleanText(question.questionText));
    setAnswerType(question.answerType);
    
    // Clean and format answer options
    if (question.answerOptions) {
      const cleanedOptions = question.answerOptions
        .replace(/[\[\]'"]+/g, '') // Remove brackets and quotes
        .split(',')
        .map(opt => opt.trim()); // Trim whitespace from each option
      setAnswerOptions(cleanedOptions);
    } else {
      setAnswerOptions([]);
    }

    // Clean and format option points
    if (question.answerType === "FreeText") {
      setOptionPoints([]);
    } else if (question.optionPoints) {
      const cleanedPoints = question.optionPoints
        .replace(/[\[\]'"]+/g, '') // Remove brackets and quotes
        .split(',')
        .map(point => Number(point.trim())); // Convert to numbers
      setOptionPoints(cleanedPoints);
    } else {
      setOptionPoints([]);
    }

    setIsEditing(true);
    setShowModal(true);
  };

  const handleAnswerTypeChange = (type) => {
    setAnswerType(type);

    if (type === "SingleOption") {
      setAnswerOptions(["Yes", "No"]);
      setOptionPoints([10, 0]);
    } else if (type === "FreeText") {
      setAnswerOptions([]);
      setOptionPoints([]);
    } else {
      setAnswerOptions([]);
      setOptionPoints([]);
    }
  };

  const handleAddOption = () => {
    if (answerOptions.length >= 10) {
      toast.error("You can only add a maximum of 10 options.");
      return;
    } else {
      setAnswerOptions([...answerOptions, ""]);
      setOptionPoints([...optionPoints, 0]);
    }
  };

  const handleDeleteOption = (index) => {
    const updatedOptions = answerOptions.filter((_, i) => i !== index);
    const updatedPoints = optionPoints.filter((_, i) => i !== index);
    setAnswerOptions(updatedOptions);
    setOptionPoints(updatedPoints);
  };

  const resetForm = () => {
    setQuestionText("");
    setAnswerType("");
    setAnswerOptions([]);
    setOptionPoints([]);
    setIsEditing(false);
    setEditId(null);
  };

  // Update handleDeleteConfirmation:
  const handleDeleteConfirmation = async () => {
    setIsDeleting(true);
    try {
      const response = await fetch(`${BASE_URL}/delete-factory-question/${deleteId}`, {
        method: "DELETE",
      });

      if (!response.ok) {
        throw new Error("Failed to delete question");
      }

      toast.success("Question deleted successfully!");
      fetchQuestions();
      setShowDeleteModal(false); // Close modal after deleting
    } catch (error) {
      showErrorModal("Delete Error", "Error deleting question. Please try again.");
      setShowDeleteModal(false); // Close modal if there's an error
    } finally {
      setIsDeleting(false);
    }
  };

  const handlePointsChange = (index, value) => {
    const newPoints = [...optionPoints];
    newPoints[index] = Number(value);
    setOptionPoints(newPoints);
  };

  const handleDownloadTemplate = () => {
    // Define the headers for the template
    const sheetData = [
      ["S/No", "Question", "Answer Type", "Answer Options", "Points"]
    ];
  
    // Populate template data (This is just a static template with sample data)
    const questions = [
      { question: "Question 1", answerType: "FreeText", answerOptions: "", points: "" },
      { question: "Question 2", answerType: "SingleOption", answerOptions: "Yes,No", points: "10,0" },
      { question: "Question 3", answerType: "MultipleOptions", answerOptions: "Option 1,Option 2,Option ", points: "5,3,2" },
      { question: "Question 4", answerType: "Dropdown", answerOptions: "item1,item2,item3,item4", points: "4,2,2,2" }
    ];
  
    // Add each question's data into the sheetData array
    questions.forEach((q, index) => {
      sheetData.push([index + 1, q.question, q.answerType, q.answerOptions, q.points]);
    });
  
    // Create the Excel file from the generated data
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(sheetData);
    XLSX.utils.book_append_sheet(wb, ws, "Questions Template");
  
    // Write the workbook to an array buffer and trigger download
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
  
    // Create a link element to download the Excel file
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "Questions_Template.xlsx";
    link.click();
  };

  // Add these states near your other state declarations
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [showMultiDeleteModal, setShowMultiDeleteModal] = useState(false);
  
  // Add this effect to handle "select all" changes
  useEffect(() => {
    if (selectAll) {
      setSelectedQuestions(filteredQuestions.map(q => q.id));
    } else if (selectedQuestions.length === filteredQuestions.length) {
      // If user manually deselected one after having all selected
      setSelectedQuestions([]);
    }
  }, [selectAll, filteredQuestions]);
  
  // Add these handler functions
  const handleSelectQuestion = (id) => {
    if (selectedQuestions.includes(id)) {
      setSelectedQuestions(prev => prev.filter(qId => qId !== id));
    } else {
      setSelectedQuestions(prev => [...prev, id]);
    }
  };
  
  const handleToggleSelectAll = () => {
    setSelectAll(!selectAll);
  };
  
  // Update handleMultiDelete:
  const handleMultiDelete = async () => {
    setIsDeleting(true);
    try {
      // Create a copy of the IDs to delete for better UX
      const idsToDelete = [...selectedQuestions];
      
      // Close the confirmation modal first
      setShowMultiDeleteModal(false);
      
      // Delete each question one by one
      let successCount = 0;
      let errorCount = 0;
      
      for (const id of idsToDelete) {
        try {
          const response = await fetch(`${BASE_URL}/delete-factory-question/${id}`, {
            method: "DELETE",
          });
          
          if (response.ok) {
            successCount++;
          } else {
            errorCount++;
          }
        } catch (error) {
          errorCount++;
        }
      }
      
      // Show results
      if (successCount > 0) {
        toast.success(`Successfully deleted ${successCount} questions.`);
      }
      if (errorCount > 0) {
        toast.error(`Failed to delete ${errorCount} questions.`);
      }
      
      // Refresh the questions list
      fetchQuestions();
      
      // Clear the selection
      setSelectedQuestions([]);
      setSelectAll(false);
      
    } catch (error) {
      showErrorModal("Delete Error", "Error deleting questions. Please try again.");
      setShowMultiDeleteModal(false);
    } finally {
      setIsDeleting(false);
    }
  };
  
  // Update the columns definition to include a selection checkbox
  const columns = React.useMemo(
    () => [
      {
        id: 'selection',
        Header: () => (
          <div onClick={(e) => {
            e.stopPropagation();
            handleToggleSelectAll();
          }}>
            {selectAll ? (
              <FaCheckSquare className="text-primary" size={18} />
            ) : (
              <FaRegSquare className="text-muted" size={18} />
            )}
          </div>
        ),
        Cell: ({ row }) => (
          <div onClick={(e) => {
            e.stopPropagation();
            handleSelectQuestion(row.original.id);
          }}>
            {selectedQuestions.includes(row.original.id) ? (
              <FaCheckSquare className="text-primary" size={18} />
            ) : (
              <FaRegSquare className="text-muted" size={18} />
            )}
          </div>
        ),
        disableSortBy: true,
        width: 40
      },
      {
        Header: 'S/No',
        accessor: (row, i) => i + 1,
        className: 'd-none d-md-table-cell',
        Cell: ({ value }) => <div className="d-none d-md-block">{value}</div>
      },
      {
        Header: 'Question',
        accessor: 'questionText',
        Cell: ({ row }) => (
          <>
            <div>{cleanText(row.original.questionText)}</div>
            <div className="d-md-none text-muted small">
              <span className="d-block">Type: {row.original.answerType}</span>
              {row.original.answerOptions && (
                <span className="d-block">
                  Options: {row.original.answerOptions.replace(/[\[\]'"]+/g, '').split(',').map(opt => opt.trim()).join(', ')}
                </span>
              )}
              {row.original.optionPoints && row.original.answerType !== 'FreeText' && (
                <span className="d-block">
                  Points: {row.original.optionPoints.replace(/[\[\]'"]+/g, '').split(',').map(point => point.trim()).join(', ')}
                </span>
              )}
            </div>
          </>
        )
      },
      {
        Header: 'Answer Type',
        accessor: 'answerType',
        className: 'd-none d-md-table-cell',
        Cell: ({ value }) => <div className="d-none d-md-block">{value}</div>
      },
      {
        Header: 'Answer Options',
        accessor: 'answerOptions',
        className: 'd-none d-lg-table-cell',
        Cell: ({ value }) => {
          if (!value) return null;
          // Remove brackets and quotes, then format with commas and spaces
          const cleanedOptions = value.replace(/[\[\]'"]+/g, '').split(',').map(opt => opt.trim()).join(', ');
          return (
            <div className="d-none d-lg-block">
              {cleanedOptions}
            </div>
          );
        }
      },
      {
        Header: 'Points',
        accessor: 'optionPoints',
        className: 'd-none d-lg-table-cell',
        Cell: ({ row, value }) => {
          if (row.original.answerType === "FreeText") {
            return <div className="d-none d-lg-block">N/A</div>;
          }
          if (!value) return null;
          // Remove brackets and format with commas and spaces
          const cleanedPoints = value.replace(/[\[\]'"]+/g, '').split(',').map(point => point.trim()).join(', ');
          return (
            <div className="d-none d-lg-block">
              {cleanedPoints}
            </div>
          );
        }
      },
      {
        Header: 'Actions',
        accessor: 'actions',
        Cell: ({ row }) => (
          <div className="btn-group">
            <Button
              variant="outline-warning"
              size="sm"
              onClick={() => handleEdit(row.original)}
            >
              <FaEdit />
            </Button>
            <Button
              variant="outline-danger"
              size="sm"
              onClick={() => {
                setDeleteId(row.original.id);
                setShowDeleteModal(true);
              }}
            >
              <FaTrash />
            </Button>
          </div>
        )
      }
    ],
    [selectAll, selectedQuestions]
  );

  // Add this before the return statement
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    state: { pageIndex, pageSize },
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    canPreviousPage,
    canNextPage,
    pageCount,
  } = useTable(
    {
      columns,
      data: filteredQuestions,
      initialState: { pageSize: 10 }
    },
    useSortBy,
    usePagination
  );

  if (!FactoryCode) {
    return (
      <div className="alert alert-danger" role="alert">
        User Company Code is missing. Please contact support.
      </div>
    );
  }

  // Just before your return statement, add this:
  const hasSelectedQuestions = selectedQuestions.length > 0;

  return (
    <div className="container-fluid p-2 p-md-4 bg-light">
      <ToastContainer autoClose={3000}/>
      
      {/* Responsive Header */}
      <div className="row align-items-center mb-3 mb-md-4 bg-white shadow-sm rounded p-2 p-md-3">
        <div className="col-12 col-md mb-3 mb-md-0">
          <h2 className="text-primary mb-0 fs-3 fs-md-2">Questions Bank</h2>
        </div>
        <div className="col-12 col-md-auto">
          <div className="d-flex flex-column flex-md-row gap-3">
            {/* Download Template Button */}
            <Button 
              variant="outline-secondary" 
              className="d-flex align-items-center justify-content-center"
              onClick={handleDownloadTemplate}
            >
              <FaDownload className="me-2" />
              Download Template
            </Button>

            {/* File Upload and Add New Container */}
            <div className="d-flex flex-column flex-md-row gap-2">
              {/* Custom File Input Container */}
              <div className="custom-file-container position-relative">
                <div className="input-group">
                  <input
                    type="file"
                    accept=".xlsx"
                    onChange={handleFileUpload}
                    className="form-control"
                    id="customFile"
                    style={{ minWidth: '220px' }}
                    disabled={isUploading}
                  />
                  <label 
                    className="input-group-text bg-white" 
                    htmlFor="customFile"
                  >
                    {isUploading ? (
                      <>
                        <div className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></div>
                        <span>Uploading...</span>
                      </>
                    ) : (
                      "Browse"
                    )}
                  </label>
                </div>
              </div>

              {/* Add New Button */}
              <Button 
                variant="primary" 
                className="d-flex align-items-center justify-content-center px-3"
                onClick={() => setShowModal(true)}
                style={{ whiteSpace: 'nowrap', minWidth: '120px' }}
              >
                <FaPlus className={`${window.innerWidth >= 768 ? 'me-2' : ''}`} />
                <span className={`${window.innerWidth < 768 ? 'd-none' : ''}`}>
                  Add New
                </span>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Responsive Search Bar */}
      <div className="mb-3 mb-md-4">
        <div className="d-flex flex-wrap gap-2 align-items-center">
          <div className="input-group shadow-sm flex-grow-1">
            <span className="input-group-text bg-white border-end-0">
              <FaSearch className="text-muted" />
            </span>
            <Form.Control
              type="text"
              placeholder="Search questions..."
              className="border-start-0"
              value={searchText}
              onChange={handleSearch}
            />
          </div>
          
          {/* Add the bulk delete button with counter */}
          {hasSelectedQuestions && (
            <Button 
              variant="danger" 
              className="d-flex align-items-center gap-2"
              onClick={() => setShowMultiDeleteModal(true)}
            >
              <FaTrash />
              <span>Delete <span className="badge bg-light text-danger">{selectedQuestions.length}</span></span>
            </Button>
          )}
        </div>
      </div>

      {/* Responsive Table */}
      <div className="card shadow-sm">
        {isLoading ? (
          <div className="text-center py-5">
            <div className="spinner-grow text-primary mb-3" style={{ width: '3rem', height: '3rem' }} role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="text-primary fw-bold mb-1">Loading Questions</p>
            <p className="text-muted">Please wait while we fetch your question data...</p>
          </div>
        ) : (
          <>
            <div className="table-responsive">
              <table {...getTableProps()} className="table table-hover align-middle mb-0">
                <thead className="table-light">
                  {headerGroups.map(headerGroup => (
                    <tr {...headerGroup.getHeaderGroupProps()}>
                      {headerGroup.headers.map(column => (
                        <th
                          {...column.getHeaderProps(column.getSortByToggleProps())}
                          className={`align-middle ${column.className || ''}`}
                          style={{
                            whiteSpace: 'nowrap',
                            minWidth: column.id === 'questionText' ? '300px' : 'auto'
                          }}
                        >
                          <div className="d-flex align-items-center">
                            {column.render('Header')}
                            {column.canSort && (
                              <span className="ms-2">
                                {column.isSorted ? (
                                  column.isSortedDesc ? (
                                    <FaSortDown />
                                  ) : (
                                    <FaSortUp />
                                  )
                                ) : (
                                  <FaSort className="text-muted" />
                                )}
                              </span>
                            )}
                          </div>
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody {...getTableBodyProps()} className="border-top-0">
                  {page.map(row => {
                    prepareRow(row);
                    return (
                      <tr {...row.getRowProps()}>
                        {row.cells.map(cell => {
                          if (cell.column.id === 'questionText') {
                            return (
                              <td {...cell.getCellProps()} style={{ minWidth: '300px' }}>
                                <div className="d-flex flex-column">
                                  <div className="fw-medium">{cleanText(cell.value)}</div>
                                  <div className="d-md-none text-muted small mt-1">
                                    <span className="d-block">Type: {row.original.answerType}</span>
                                    {row.original.answerOptions && (
                                      <span className="d-block">Options: {cleanText(row.original.answerOptions).split(',').join(', ')}</span>
                                    )}
                                    {row.original.optionPoints && row.original.answerType !== 'FreeText' && (
                                      <span className="d-block">Points: {cleanText(row.original.optionPoints).split(',').join(', ')}</span>
                                    )}
                                  </div>
                                </div>
                              </td>
                            );
                          }
                          if (cell.column.id === 'actions') {
                            return (
                              <td {...cell.getCellProps()} style={{ whiteSpace: 'nowrap' }}>
                                <div className="d-flex gap-2">
                                  <Button
                                    variant="outline-warning"
                                    size="sm"
                                    onClick={() => handleEdit(row.original)}
                                    className="d-flex align-items-center"
                                  >
                                    <FaEdit className="me-md-2" />
                                    <span className="d-none d-md-inline">Edit</span>
                                  </Button>
                                  <Button
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={() => {
                                      setDeleteId(row.original.id);
                                      setShowDeleteModal(true);
                                    }}
                                    className="d-flex align-items-center"
                                  >
                                    <FaTrash className="me-md-2" />
                                    <span className="d-none d-md-inline">Delete</span>
                                  </Button>
                                </div>
                              </td>
                            );
                          }
                          return (
                            <td {...cell.getCellProps()} className={cell.column.className}>
                              {cell.render('Cell')}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            
            {/* Pagination - Only show when not loading */}
            <div className="d-flex justify-content-between align-items-center p-3 border-top">
              <div className="d-flex align-items-center gap-2">
                <select
                  className="form-select form-select-sm"
                  value={pageSize}
                  onChange={e => setPageSize(Number(e.target.value))}
                  style={{ width: '100px' }}
                >
                  {[10, 20, 30, 40, 50].map(pageSize => (
                    <option key={pageSize} value={pageSize}>
                      Show {pageSize}
                    </option>
                  ))}
                </select>
                <span className="text-muted small">
                  Page {pageIndex + 1} of {pageCount}
                </span>
              </div>
              <div className="btn-group">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => gotoPage(0)}
                  disabled={!canPreviousPage}
                >
                  {'<<'}
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => previousPage()}
                  disabled={!canPreviousPage}
                >
                  {'<'}
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => nextPage()}
                  disabled={!canNextPage}
                >
                  {'>'}
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => gotoPage(pageCount - 1)}
                  disabled={!canNextPage}
                >
                  {'>>'}
                </Button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Update Modal size based on screen */}
      <Modal 
        show={showModal} 
        onHide={() => setShowModal(false)} 
        centered 
        size="lg"
        fullscreen="sm-down"
      >
        <Modal.Header closeButton>
          <Modal.Title>{isEditing ? "Edit Question" : "Add New Question"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Question</Form.Label>
              <Form.Control
                type="text"
                value={questionText}
                onChange={(e) => setQuestionText(e.target.value)}
                required
                placeholder="Enter question text"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Answer Type</Form.Label>
              <div className="d-flex align-items-center">
                <Form.Select
                  value={answerType}
                  onChange={(e) => handleAnswerTypeChange(e.target.value)}
                  required
                  className="me-2"
                >
                  <option value="">Select Answer Type</option>
                  <option value="SingleOption">Single Option</option>
                  <option value="MultipleOptions">Multiple Options</option>
                  <option value="Dropdown">Dropdown</option>
                  <option value="FreeText">Free Text</option>
                </Form.Select>
                {answerType !== "FreeText" && answerType !== "SingleOption" && (
                  <Button
                    variant="success"
                    size="sm"
                    onClick={handleAddOption}
                  >
                    <FaPlus className="me-1" /> Add Option
                  </Button>
                )}
              </div>
            </Form.Group>
            {answerOptions.length > 0 && answerType !== "FreeText" && (
              <Form.Group className="mb-3">
                <Form.Label>Answer Options and Points (Total: 10 points)</Form.Label>
                {answerOptions.map((option, index) => (
                  <div key={index} className="d-flex align-items-center mb-2">
                    <Form.Control
                      type="text"
                      value={option}
                      placeholder={`Option ${index + 1}`}
                      readOnly={answerType === "SingleOption"}
                      onChange={(e) => {
                        const updatedOptions = [...answerOptions];
                        updatedOptions[index] = e.target.value;
                        setAnswerOptions(updatedOptions);
                      }}
                      className="me-2"
                    />
                    <Form.Control
                      type="number"
                      value={optionPoints[index]}
                      min="0"
                      max="10"
                      style={{ width: "80px" }}
                      onChange={(e) => handlePointsChange(index, e.target.value)}
                      className="me-2"
                    />
                    {answerType !== "SingleOption" && (
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => handleDeleteOption(index)}
                      >
                        <FaTrash />
                      </Button>
                    )}
                  </div>
                ))}
                <div className="text-muted mt-2">
                  Total Points: {optionPoints.reduce((sum, points) => sum + Number(points), 0)}/10
                </div>
              </Form.Group>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? (
              <>
                <div className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></div>
                <span>{isEditing ? "Updating..." : "Saving..."}</span>
              </>
            ) : (
              "Save"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Make other modals responsive */}
      <Modal 
        show={showDeleteModal} 
        onHide={() => setShowDeleteModal(false)} 
        centered
        size="sm"
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete this question? This action cannot be undone.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={() => setShowDeleteModal(false)}
          >
            Cancel
          </Button>
          <Button 
            variant="danger" 
            onClick={handleDeleteConfirmation}
            disabled={isDeleting}
          >
            {isDeleting ? (
              <>
                <div className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></div>
                <span>Deleting...</span>
              </>
            ) : (
              "Delete"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal 
        show={errorModal.show} 
        onHide={() => setErrorModal({ show: false, title: "", message: "" })}
        centered
        size="sm"
      >
        <Modal.Header closeButton>
          <Modal.Title className="text-danger">{errorModal.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{errorModal.message}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={() => setErrorModal({ show: false, title: "", message: "" })}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Add the multi-delete confirmation modal */}
      <Modal 
        show={showMultiDeleteModal} 
        onHide={() => setShowMultiDeleteModal(false)} 
        centered
        size="sm"
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Multiple Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete <strong>{selectedQuestions.length}</strong> selected questions? This action cannot be undone.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={() => setShowMultiDeleteModal(false)}
          >
            Cancel
          </Button>
          <Button 
            variant="danger" 
            onClick={handleMultiDelete}
            disabled={isDeleting}
          >
            {isDeleting ? (
              <>
                <div className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></div>
                <span>Deleting...</span>
              </>
            ) : (
              "Delete Selected"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

            <style jsx>{`
        /* Enhanced loading animation */
        .spinner-grow {
          animation-duration: 1.5s;
        }
        
        .text-primary.fw-bold {
          font-size: 1.2rem;
        }
        
        /* Fadeout animation for when loading completes */
        @keyframes fadeOut {
          from { opacity: 1; }
          to { opacity: 0; }
        }
        
        .loading-fadeout {
          animation: fadeOut 0.5s ease-out forwards;
        }
        
        /* Other existing styles... */
      `}</style>
    </div>
  );
};

export default FactoryQuestionsPage;
